import { Fragment, PointerEvent, ReactElement, ReactNode } from 'react'
import { Vector3 } from 'three'
import { Box, Divider, IconButton, Paper, SxProps, Theme, Tooltip } from '@mui/material'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import { Html } from '@react-three/drei'

export interface SceneHudAction {
  /** Stable key (also used for the React key). */
  key: string
  tooltip: string
  icon: ReactNode
  /** Click handler for instant actions (edit, copy, delete, …). */
  onClick?: () => void
  /** Pointer-down handler for press-and-drag actions (move). */
  onPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void
  /** Keep the button in its filled state while its action is in progress. */
  active?: boolean
  /**
   * Persistent on/off state for toggle buttons: fills the button like `active` but does NOT count
   * towards the "an action is in progress" state, so it neither disables nor is disabled by others.
   */
  toggled?: boolean
  /** `danger` fills error-red instead of primary on hover/active. */
  variant?: 'default' | 'danger'
  /** Disable the button. The tooltip still shows (the button is wrapped in a span). */
  disabled?: boolean
  /** Show a spinner instead of the icon (and disable the button) while its request runs. */
  loading?: boolean
  dataCy?: string
}

interface Props {
  /** World-space anchor the bar is positioned at (inside the r3f scene). */
  position: Vector3
  /** Action groups; a vertical divider is drawn between non-empty groups. */
  groups: SceneHudAction[][]
  /** CSS transform positioning the bar relative to its anchor. */
  transform?: string
  zIndexRange?: [number, number]
  /** Dwell time (ms) before a tooltip appears. */
  tooltipEnterDelayMs?: number
  /**
   * Fires on pointer-down anywhere on the bar. R3F still reports HUD clicks as
   * scene "misses", so the owner can use this to suppress a deselect triggered
   * by interacting with the HUD.
   */
  onBarPointerDown?: () => void
}

// Single rounded "pill" bar. Buttons are ghost icons that fill on hover (or
// while active); the destructive `danger` variant fills error-red instead.
const barSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.25,
  px: 0.5,
  py: 0.5,
  borderRadius: 1.5,
  bgcolor: 'background.paper',
}

const buttonSx = (active: boolean, danger: boolean): SxProps<Theme> => {
  const fill = danger ? 'error.main' : 'primary.main'
  return {
    color: active ? 'common.white' : 'text.secondary',
    bgcolor: active ? fill : 'transparent',
    borderRadius: 1.5,
    transition: 'background-color 150ms ease, color 150ms ease',
    '&:hover': { bgcolor: fill, color: 'common.white' },
  }
}

/**
 * Floating action bar anchored to a point in the 3D scene, rendered through a
 * drei `<Html>` portal. Shared by the comment controls and the callout controls
 * so both get the same look (pill, ghost buttons, dwell-delayed tooltips,
 * active/destructive states).
 *
 * The portal renders outside the r3f tree, so the app's MUI theme context
 * doesn't reach it — we re-provide it from the theme available here.
 */
const SceneHud = ({
  position,
  groups,
  transform = 'translate(-50%, 10px)',
  zIndexRange = [90, 0],
  tooltipEnterDelayMs = 1000,
  onBarPointerDown,
}: Props): ReactElement => {
  const theme = useTheme()
  const visibleGroups = groups.filter(group => group.length > 0)

  const anyActionActive = visibleGroups.some(group => group.some(action => action.active))

  return (
    <Html position={position} zIndexRange={zIndexRange} style={{ pointerEvents: 'auto' }}>
      <ThemeProvider theme={theme}>
        <Paper
          elevation={4}
          sx={{ ...barSx, transform }}
          onPointerDown={event => {
            event.stopPropagation()
            onBarPointerDown?.()
          }}
        >
          {visibleGroups.map((group, groupIndex) => (
            <Fragment key={group[0].key}>
              {groupIndex > 0 && (
                <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />
              )}
              {group.map(action => (
                <Tooltip
                  key={action.key}
                  title={action.tooltip}
                  enterDelay={tooltipEnterDelayMs}
                  enterNextDelay={tooltipEnterDelayMs}
                >
                  {/* Wrapped in a span so the tooltip still shows while the button
                      is disabled (a disabled MUI button emits no pointer events). */}
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <IconButton
                      size="small"
                      sx={buttonSx(
                        (action.active ?? false) || (action.toggled ?? false),
                        action.variant === 'danger',
                      )}
                      onClick={action.onClick}
                      onPointerDown={action.onPointerDown}
                      data-cy={action.dataCy}
                      disabled={action.disabled || (!action.active && anyActionActive)}
                      loading={action.loading}
                      className="scene-hud-button"
                    >
                      {action.icon}
                    </IconButton>
                  </Box>
                </Tooltip>
              ))}
            </Fragment>
          ))}
        </Paper>
      </ThemeProvider>
    </Html>
  )
}

export default SceneHud
